/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { dashboardActions } from 'modules/dashboard/slice';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectNotification } from 'modules/common/notifications/selectors';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { isEmpty } from 'modules/common/helpers/object';
import { notificationActions } from 'modules/common/notifications/slice';
import Row from 'modules/common/components/add-hotel-group/components/load-filter/row';
import { Div } from 'modules/common/components/add-hotel-group/style';
import { selectHotelGroupsList } from 'modules/dashboard/selectors';
import { Search } from 'react-feather';

/** Implementation to load saved hotel set combination
 * @param {Boolean} openLoad - To open/ close dialog box
 * @param {Function} listFilters  - Function to get new filter list for the selected page
 * @param {Function} setLoadFilter - Function to set load filter true when the saved filter is clicked to be loaded
 * @param {Array} breakdown - Selected breakdown
 * @param {Function} onClose - Function to trigger on close event
 * @returns
 */
const LoadFilter = ({ openLoad, onClose, setLoadFilter }) => {
  const dispatch = useDispatch();
  // Notification, filters, organization id and current user values are selected from store here. Refer each selector function for details
  const notification = useSelector(selectNotification);
  const filters = useSelector(selectHotelGroupsList);
  const organizationId = useSelector(selectOrganizationId);
  const currentUser = useSelector(selectAuthUser);

  // Local state to control pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParam, setSearchTerm] = useState('');
  //
  const modalAction = () => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    setSearchTerm('');
    onClose();
  };
  // Get paginated saved filter list for custom grouping load filter
  const listFilters = (page) => {
    const query = `limit=${PAGINATION.AGGREGATION_FILTER_PER_PAGE}&page=${page}&hotelGroupName=${searchParam}&searchEnabled=true`;
    dispatch(
      dashboardActions.getLoadHotelGroupList({ organizationId, userId: currentUser?.id, query })
    );
  };
  //
  useEffect(() => {
    setCurrentPage(0, listFilters(0));
  }, [searchParam]);
  //
  const deleteFilters = (filterId) => {
    const query = `hotelGroupName=${filterId}`;
    dispatch(
      dashboardActions.deleteHotelGroup({
        organizationId,
        userId: currentUser?.id,
        query,
      })
    );
  };
  //
  return (
    <Dialog open={openLoad} onClose={onClose} aria-labelledby="form-dialog-title" fullWidth>
      <DialogTitle id="form-dialog-title">Hotel Sets</DialogTitle>
      <DialogContent>
        <Grid
          container
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          m={1}
          mb={2}
        >
          <TextField
            placeholder="Search"
            fullWidth
            size="small"
            value={searchParam}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            variant="standard"
          />
        </Grid>
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Hotel Set Name</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty(filters?.results) && filters?.results?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Div>
                      No saved hotel groups.
                      <br />
                      <span>Please save hotel groups before selecting</span>
                    </Div>
                  </TableCell>
                </TableRow>
              ) : (
                !isEmpty(filters?.results) &&
                filters?.results?.map((filter, index) => (
                  <Row
                    // eslint-disable-next-line no-underscore-dangle, react/no-array-index-key
                    key={`${index}-${filter?._id}`}
                    hotels={filter?.hotelGroups}
                    onClose={onClose}
                    setLoadFilter={setLoadFilter}
                    deleteFilters={deleteFilters}
                  />
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5]}
                  count={filters?.totalCount ?? 0}
                  rowsPerPage={5}
                  page={currentPage}
                  onPageChange={(event, newPage) => {
                    setCurrentPage(newPage, listFilters(newPage));
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <DialogActions>
          <Button
            variant="contained"
            sx={{ marginTop: 2 }}
            color="primary"
            onClick={() => modalAction()}
          >
            Cancel
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
//
export default LoadFilter;
